/**
 * Role → ReBAC mapping (NEX-2514 RBAC enforcement slice).
 *
 * `RoleResourceType` (role.types.ts) and `ResourceType` (permissions.types.ts)
 * are deliberately separate vocabularies — see role.types.ts's own doc comment.
 * This file is the one place that bridges them, so the two never drift into an
 * ad-hoc string comparison at a call site.
 *
 * Frontend-safe: zero Prisma runtime dependency.
 */
import { AppFeature, type AppFeatureKey } from "../features/features.constants";
import { ResourceType } from "../permissions/permissions.types";
import {
  PERMISSION_SET_SURFACE_WILDCARD,
  type PermissionSetSurfaceValue
} from "./permission-set-surface.types";
import { ROLE_RESOURCE_TYPES, RoleResourceType } from "./role.types";

/**
 * Which `RoleResourceType`s the base ReBAC engine already guards, and what
 * `ResourceType` each maps to. `null` is "no base guard exists for this kind",
 * stated per member rather than left out.
 *
 * 🚨 TOTAL, NOT `Partial`, AND THAT IS THE WHOLE POINT OF THE `null`s. This map
 * is where a member with no mapping turned an approval into a silent no-op, and
 * a `Partial<Record<…>>` gives the compiler nothing to say when the union grows:
 * a sixth `RoleResourceType` would map to nothing, everywhere, with `pnpm
 * typecheck` green. Written total, adding a member is a compile error HERE — in
 * the one file that documents what "unmapped" costs — until somebody states
 * which of the two answers it gets.
 *
 * AI_TASK is `null` because it has no `@RequirePermission`-guarded controller
 * anywhere in the codebase today (a reserved `ResourceType.KNOWLEDGE` value
 * exists for a Collection, but zero call sites use it). Role membership cannot
 * gate access that no base guard enforces in the first place — that is
 * prerequisite infrastructure this mapping does not invent, since it is not
 * Role-specific. A resource of a `null` type stays exactly as open as it was
 * before this slice.
 *
 * ⚠️ THE THREE GRANTABLE KINDS ARE NOT UNMAPPED — THEY ARE NOT IN THIS MAP AT
 * ALL, AND THE TWO STATES ARE DIFFERENT. `collection`, `workspace` and
 * `external_tool` reach a Role through `RoleCollectionGrant`,
 * `RoleWorkspaceGrant` and `RoleExternalToolGrant`, so they were never
 * `RoleResource` rows for `RolePermissionSyncService` to sync. Adding one here
 * would not widen the permission layer; it would only make this map describe a
 * table the grant never writes.
 *
 * Extending coverage: once a RoleResourceType's controller gets its own
 * `@RequirePermission(ResourceType.X, ...)` wiring, replace that member's `null`
 * with the pairing and `RolePermissionSyncService` picks it up automatically —
 * no other change.
 */
export const ROLE_RESOURCE_TYPE_TO_RESOURCE_TYPE: Record<RoleResourceType, ResourceType | null> = {
  [RoleResourceType.AGENT]: ResourceType.AGENT,
  [RoleResourceType.WORKFLOW]: ResourceType.WORKFLOW,
  [RoleResourceType.DEPLOYMENT]: ResourceType.DEPLOYMENT,
  [RoleResourceType.DOCUMENT_TEMPLATE]: ResourceType.TEMPLATE,
  [RoleResourceType.AI_TASK]: null
};

/**
 * Can a grant on this `RoleResourceType` actually reach the permission layer?
 *
 * DERIVED from {@link ROLE_RESOURCE_TYPE_TO_RESOURCE_TYPE}, never hand-listed, so
 * a type gaining its `@RequirePermission` wiring becomes grantable by adding the
 * one pairing above and nothing else — the same "no other change" property that
 * mapping's own doc promises for `RolePermissionSyncService`.
 *
 * 🚨 THE NO-OP THIS EXISTS TO SEPARATE IS SAFE ON ONE PATH AND A LIE ON THE OTHER,
 * AND BOTH PATHS RUN THROUGH THE SAME SERVICE METHOD SHAPE. Attaching a resource
 * to a Role writes no grant for an unmapped type, which is correct and invisible:
 * nothing was gated before and nothing is gated after, and no human was told
 * otherwise. APPROVING AN ACCESS REQUEST is the opposite — a person asked, a second
 * person decided, and the decision is recorded as APPROVED while writing no grant
 * at all. The record then reads as access administered, in a queue whose entire
 * purpose is to be that record.
 *
 * So an unmapped type is a legitimate silence for an attach and a refusal for a
 * request: see `CreateRoleResourceAccessRequestUseCase`, which declines to open a
 * request that no approval could ever honour.
 */
export const isGrantableRoleResourceType = (roleResourceType: RoleResourceType): boolean =>
  ROLE_RESOURCE_TYPE_TO_RESOURCE_TYPE[roleResourceType] !== null;

/**
 * The inverse of {@link ROLE_RESOURCE_TYPE_TO_RESOURCE_TYPE}, COMPUTED rather
 * than hand-listed so the two can never disagree.
 *
 * It exists because `RoleResource.resourceType` stores a `RoleResourceType`
 * while every permission-layer caller holds a `ResourceType`, and for the four
 * mapped pairings the two vocabularies happen to spell the values identically.
 * That coincidence is not a contract: `RoleResourceType.DOCUMENT_TEMPLATE`
 * ("document_template") already maps to `ResourceType.TEMPLATE` ("template"),
 * so a query passing a `ResourceType` straight into a `RoleResource` filter is
 * ALREADY wrong for that pair and merely silent about it — it matches zero rows
 * and reads as "this Role holds no templates".
 *
 * Use this to cross the boundary. `undefined` means the `ResourceType` is not a
 * Role resource type at all, which denies rather than widens.
 */
export const RESOURCE_TYPE_TO_ROLE_RESOURCE_TYPE: Partial<Record<ResourceType, RoleResourceType>> =
  ROLE_RESOURCE_TYPES.reduce<Partial<Record<ResourceType, RoleResourceType>>>(
    (inverse, roleResourceType) => {
      const resourceType = ROLE_RESOURCE_TYPE_TO_RESOURCE_TYPE[roleResourceType];
      if (resourceType !== null) inverse[resourceType] = roleResourceType;
      return inverse;
    },
    {}
  );

/**
 * Which `ResourceType`s a group grant's `surface` (an `AppFeatureKey`) can
 * stand in for, when deciding whether a group's narrower-than-full-Role grant
 * covers one specific resource. Only unambiguous 1:1 pairings get a surface;
 * every other member is `null`, stated rather than left out.
 *
 * 🚨 TOTAL FOR THE SAME REASON THE MAPPING ABOVE IS. A new `ResourceType` added
 * with no entry here reaches NOTHING through any group grant — the grant row is
 * written, the API answers 201, and enforcement never matches it. That failure
 * is silent in both directions and a `Partial<Record<…>>` is what makes it
 * silent. Total, a new `ResourceType` is a compile error here until somebody
 * says whether a surface covers it.
 *
 * `ResourceType.TEMPLATE` (document_template) is deliberately `null`, and so is
 * `ResourceType.DOCUMENT`. `AppFeature.DOCUMENTS` ("Document library") is the
 * closest name for either, and it is NOT a 1:1 pairing with either one: it is
 * the gate on `@Controller("documents")` AND on `@Controller("document-templates")`,
 * so mapping it here would let one group grant stand in for two resource types
 * that the product treats as separate — wrongly gating a group out of resources
 * it should see, or wrongly letting one in. A grant naming a surface no member
 * maps to simply doesn't cover any resource type; the individual Role-membership
 * path (owner/admin/member) is unaffected.
 *
 * 🚨 THE SURFACE VOCABULARY IS NOT 1:1 WITH THE FEATURE CATALOG, AND READING IT
 * AS THOUGH IT WERE IS HOW A WRONG PAIRING GETS ARGUED FOR. `AppFeature` names
 * NAV DESTINATIONS; `ResourceType` names things a permission row points at. One
 * feature can gate several destinations (`DOCUMENTS`, above), and a feature can
 * gate a destination while enforcing nothing on the server at all — which is
 * `AppFeature.KNOWLEDGE` today, named by the rail row over `/app/documents` and
 * by no `@RequireFeatureAccess` anywhere. The frontend's
 * `nav-feature-gates-match-the-server.test.ts` is the ledger of those, held against
 * `SERVER_ENFORCED_APP_FEATURES`, so this paragraph cannot go stale unobserved.
 *
 * "A CS staff group gets inbox access only" (Assem's example) has no
 * `ResourceType` counterpart at all (inbox/conversations aren't a Role
 * resource type) — not mappable here, same as the two `null`
 * RoleResourceTypes above. That is why {@link isGrantablePermissionSetSurface}
 * exists: the write doors refuse the surface rather than storing a grant
 * nothing can honour.
 */
export const RESOURCE_TYPE_TO_SURFACE: Record<ResourceType, AppFeatureKey | null> = {
  [ResourceType.AGENT]: AppFeature.AGENTS.key,
  [ResourceType.WORKFLOW]: AppFeature.WORKFLOWS.key,
  [ResourceType.DEPLOYMENT]: AppFeature.DEPLOYMENTS.key,
  [ResourceType.KNOWLEDGE]: null,
  [ResourceType.CREDENTIAL]: null,
  [ResourceType.ACCESS_CARD]: null,
  [ResourceType.TEMPLATE]: null,
  [ResourceType.DOCUMENT]: null,
  [ResourceType.FEATURE]: null,
  [ResourceType.VIBE_APP]: null,
  [ResourceType.WORKSPACE]: null,
  /**
   * `null`, and the reason is that there is nothing else this could be: no
   * `AppFeature` names tracks. A permission set opens a SURFACE, and a surface
   * is a nav destination in the feature catalog — so a resource type with no
   * feature has no surface for a set to open, and a grant naming one is a row
   * nothing can honour.
   *
   * ⚠️ MINTING AN `AppFeature.TRACKS` LATER DOES NOT MAKE THIS SAFE TO CHANGE.
   * {@link GRANTABLE_PERMISSION_SET_SURFACES} is COMPUTED from the non-null values
   * here, so pairing a surface with `track` makes that surface grantable in the
   * same edit — a widening, not a label. It would also be inert until something
   * else lands: the role-grant read path resolves the owning Role through
   * `RESOURCE_TYPE_TO_ROLE_RESOURCE_TYPE`, which carries no `track` entry, so
   * `findRoleOwningResource` returns null and the arm can never allow. A pairing
   * added on its own is therefore a widening that LOOKS inert while it is
   * waiting for a second change to arm it.
   */
  [ResourceType.TRACK]: null
};

/**
 * The surfaces a group grant can name that any resource type actually resolves
 * to. COMPUTED from {@link RESOURCE_TYPE_TO_SURFACE}, never hand-listed, so a
 * new pairing there makes that surface grantable with no other change — the
 * same property {@link isGrantableRoleResourceType} has over its own map.
 */
export const GRANTABLE_PERMISSION_SET_SURFACES: ReadonlySet<AppFeatureKey> = new Set(
  Object.values(RESOURCE_TYPE_TO_SURFACE).filter(
    (surface): surface is AppFeatureKey => surface !== null
  )
);

/**
 * Can a group grant on this surface ever reach a resource?
 *
 * 🚨 THE ANSWER IS `false` FOR MOST OF THE `AppFeatureKey` CATALOG, AND BOTH
 * WRITE DOORS USED TO ACCEPT ALL OF IT. Enforcement — `FindPermissionSetSurfaceAccess`
 * and `ListSurfaceGrantedRoleIds`, the only two readers — matches a stored
 * `surface` by EXACT EQUALITY against `RESOURCE_TYPE_TO_SURFACE[resourceType]`.
 * So a grant naming `inbox`, `documents`, `knowledge`, `tools`, … is written,
 * answered 201, listed back as a grant, and reaches nothing for the life of the
 * row. Nobody is told, and there is nothing to see in a log.
 *
 * That is the same shape `isGrantableRoleResourceType` refuses one table over: a
 * union member that maps to nothing, and a write that reports success. The
 * v1 contract already DOCUMENTS the narrow answer — *"a STRICT ALLOW-LIST over
 * the surface-mapped resource types (agents, workflows, deployments)"* — so this
 * predicate brings the code up to what the published contract already promises.
 *
 * The wildcard is grantable: it covers every mapped surface by definition.
 */
export const isGrantablePermissionSetSurface = (surface: PermissionSetSurfaceValue): boolean =>
  surface === PERMISSION_SET_SURFACE_WILDCARD || GRANTABLE_PERMISSION_SET_SURFACES.has(surface);
